import { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Check } from 'lucide-react';
import { fadeUp, viewportOnce } from './Animations';
import { program } from '@/lib/program-data';

export default function AdmissionJourney({ onApplyClick }: { onApplyClick: () => void }) {
  const [activeStep, setActiveStep] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start 0.75', 'end 0.4'],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  const active = program.admissionJourney.find((s) => s.step === activeStep) ?? program.admissionJourney[0];

  return (
    <section className="section-padding bg-edept-dark text-white">
      <div className="container-content">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-edept-orange">
            Admission journey with edept
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl">Seven steps, fully guided</h2>
          <p className="mt-4 text-lg text-white/75">
            edept manages a structured, transparent admission and transition process in collaboration
            with Illinois Tech and Mahindra University. <br /> Click any step to see the
            details.
          </p>
        </motion.div>

        <div ref={containerRef} className="mx-auto mt-16 grid max-w-5xl gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          {/* Interactive vertical timeline / stepper */}
          <div className="relative pl-2">
            <div className="absolute left-[35px] top-4 h-[calc(100%-2rem)] w-[3px] rounded-full bg-white/10" aria-hidden="true" />
            {/* <motion.div
              className="absolute left-[35px] top-1 w-[3px] origin-top rounded-full bg-edept-orange"
              style={{ height: lineHeight }}
              aria-hidden="true"
            /> */}

            <ol className="relative space-y-2">
              {program.admissionJourney.map((item) => {
                const isActive = item.step === activeStep;
                const isPast = item.step < activeStep;
                return (
                  <li key={item.step}>
                    <button
                      type="button"
                      onClick={() => setActiveStep(item.step)}
                      aria-current={isActive}
                      className="group flex w-full items-center gap-4 rounded-xl px-2 py-3 text-left transition-colors hover:bg-white/5"
                    >
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 font-heading text-sm transition-colors ${
                          isActive
                            ? 'border-edept-orange bg-edept-orange text-white'
                            : isPast
                              ? 'border-edept-orange/70 bg-[#f0ebe5] text-edept-orange'
                              : 'border-white/25 bg-white/80 text-edept-dark'
                        }`}
                      >
                        {isPast ? <Check size={18} aria-hidden="true" /> : String(item.step).padStart(2, '0')}
                      </span>
                      <span
                        className={`text-base font-semibold transition-colors ${
                          isActive ? 'text-white' : 'text-white/60 group-hover:text-white/85'
                        }`}
                      >
                        {item.title}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Active step detail panel */}
          <motion.div
            key={active.step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="h-fit rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur lg:sticky lg:top-28"
          >
            <span className="inline-flex items-center rounded-full bg-edept-orange/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-edept-orange">
              Step {active.step} of {program.admissionJourney.length}
            </span>
            <h3 className="mt-4 text-2xl text-white">{active.title}</h3>
            <p className="mt-3 text-lg leading-relaxed text-white/80">{active.description}</p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setActiveStep((s) => Math.max(1, s - 1))}
                disabled={active.step === 1}
                className="rounded-full border border-white/25 px-5 py-2.5 text-sm font-semibold text-white/80 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
              >
                Previous
              </button>
              {active.step < program.admissionJourney.length ? (
                <button
                  type="button"
                  onClick={() => setActiveStep((s) => Math.min(program.admissionJourney.length, s + 1))}
                  className="rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-edept-dark transition-transform hover:scale-[1.02]"
                >
                  Next step
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onApplyClick}
                  className="rounded-full bg-edept-orange px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
                >
                  Start your application
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
